import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Form, Card } from "react-bootstrap";
import { getStates, getDistricts, getInstitutions } from "../../api/services/masterService";
import {
  StyledFormGroup, Feedback,
  SCHOOL_FIXED_FEE, EXAM_FEES,
  UG_ELIGIBILITY, PG_ELIGIBILITY, ALL_EXAM_LEVELS,
  LEVELS, getDetailsPath
} from "./education/Common";
import SchoolForm from "./education/SchoolForm";
import UGForm from "./education/UGForm";
import PGForm from "./education/PGForm";
import IndividualForm from "./education/IndividualForm";
import ExamSelection from "./education/ExamSelection";

const EducationInformation = ({ register, setValue, watch, errors }) => {
  const level = watch("level");

  // Dynamic Path based on Level
  const detailsPath = useMemo(() => getDetailsPath(level), [level]);

  // Watchers
  const selectedState = watch(`${detailsPath}.state`);
  const selectedDistrict = watch(`${detailsPath}.district`);
  const selectedGrade = watch("schoolDetails.grade");
  const ugYear = watch("ugDetails.year");
  const pgYear = watch("pgDetails.year");

  // Local State
  const [selectedSpecialization, setSelectedSpecialization] = useState("");
  const [selectedExamLevel, setSelectedExamLevel] = useState("");
  const [examSelections, setExamSelections] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [institutions, setInstitutions] = useState([]);

  // --- Effects ---

  // 1. Fetch States (once)
  useEffect(() => {
    let mounted = true;
    getStates().then(data => {
      if (mounted) setStates(data);
    }).catch(console.error);
    return () => { mounted = false; };
  }, []);

  // 2. Fetch Districts when State changes
  useEffect(() => {
    if (!selectedState) {
      setDistricts([]);
      return;
    }

    // Reset fields if state changes
    setValue(`${detailsPath}.district`, "");
    setValue(`${detailsPath}.collegeName`, "");

    getDistricts(selectedState).then(setDistricts).catch(console.error);
  }, [selectedState, detailsPath, setValue]);

  // 3. Fetch Institutions when District changes
  useEffect(() => {
    if (!selectedState || !selectedDistrict || !level) {
      setInstitutions([]);
      return;
    }

    setValue(`${detailsPath}.collegeName`, "");

    getInstitutions({
      state: selectedState,
      district: selectedDistrict,
      level
    }).then(setInstitutions).catch(console.error);
  }, [selectedDistrict, selectedState, level, detailsPath, setValue]);

  // 4. Handle Level Change (Reset costs)
  useEffect(() => {
    if (level === LEVELS.SCHOOL) {
      setTotalAmount(SCHOOL_FIXED_FEE);
      setValue("totalAmount", SCHOOL_FIXED_FEE);
      setValue("examSelections", []);
    } else {
      setTotalAmount(0);
      setValue("totalAmount", 0);
      setExamSelections([]);
      setValue("examSelections", []);
    }
  }, [level, setValue]);

  // --- Handlers ---

  const handleInstitutionAdded = (newInst) => {
    setInstitutions(prev => [...prev, newInst]);
  };

  const handleAddSelection = () => {
    if (!selectedSpecialization || !selectedExamLevel) return;
    const fee = EXAM_FEES[selectedExamLevel] || 0;

    const newItem = { specialization: selectedSpecialization, examLevel: selectedExamLevel, fee };
    const updatedList = [...examSelections, newItem];

    setExamSelections(updatedList);
    setValue("examSelections", updatedList);

    const total = updatedList.reduce((sum, i) => sum + i.fee, 0);
    setTotalAmount(total);
    setValue("totalAmount", total);

    // Reset selection inputs
    setSelectedSpecialization("");
    setSelectedExamLevel("");
    setValue("specialization", "");
    setValue("examLevel", "");
  };

  const handleRemoveSelection = (index) => {
    const updatedList = examSelections.filter((_, i) => i !== index);
    setExamSelections(updatedList);
    setValue("examSelections", updatedList);

    const total = updatedList.reduce((sum, item) => sum + item.fee, 0);
    setTotalAmount(total);
    setValue("totalAmount", total);
  };

  // Memoized Eligible Levels
  const eligibleExamLevels = useMemo(() => {
    if (level === LEVELS.UG) return UG_ELIGIBILITY[ugYear] || [];
    if (level === LEVELS.PG) return PG_ELIGIBILITY[pgYear] || [];
    if (level === LEVELS.INDIVIDUAL) return ALL_EXAM_LEVELS;
    return [];
  }, [level, ugYear, pgYear]);

  // Get errors for current section dynamically
  const sectionErrors = errors[detailsPath] || {};

  return (
    <>
      <h5 className="section-title">Education Information</h5>

      <Row>
        <StyledFormGroup as={Col} md="4">
          <Form.Label>Level <span className="text-danger">*</span></Form.Label>
          <Form.Select {...register("level")} isInvalid={!!errors.level}>
            <option value="">Select Level</option>
            <option value="school">School</option>
            <option value="ug">UG</option>
            <option value="pg">PG</option>
            <option value="individual">Individual</option>
          </Form.Select>
          <Feedback error={errors.level} />
        </StyledFormGroup>
      </Row>

      {/* --- FORM SECTIONS --- */}

      {level === LEVELS.SCHOOL && (
        <SchoolForm
          register={register}
          sectionErrors={sectionErrors}
          detailsPath={detailsPath}
          states={states}
          districts={districts}
          institutions={institutions}
          setValue={setValue}
          onInstitutionAdded={handleInstitutionAdded}
          selectedState={selectedState}
          selectedDistrict={selectedDistrict}
          level={level}
        />
      )}

      {level === LEVELS.UG && (
        <UGForm
          register={register}
          sectionErrors={sectionErrors}
          detailsPath={detailsPath}
          states={states}
          districts={districts}
          institutions={institutions}
          setValue={setValue}
          onInstitutionAdded={handleInstitutionAdded}
          selectedState={selectedState}
          selectedDistrict={selectedDistrict}
          level={level}
        />
      )}

      {level === LEVELS.PG && (
        <PGForm
          register={register}
          sectionErrors={sectionErrors}
          detailsPath={detailsPath}
          states={states}
          districts={districts}
          institutions={institutions}
          setValue={setValue}
          onInstitutionAdded={handleInstitutionAdded}
          selectedState={selectedState}
          selectedDistrict={selectedDistrict}
          level={level}
        />
      )}

      {level === LEVELS.INDIVIDUAL && (
        <IndividualForm
          register={register}
          sectionErrors={sectionErrors}
          detailsPath={detailsPath}
        />
      )}

      {/* --- SUMMARY / EXAM SELECTION --- */}

      {level === LEVELS.SCHOOL && selectedGrade && (
        <Card className="mt-3 p-3">
          <h6>Total Amount</h6>
          <strong>₹{SCHOOL_FIXED_FEE}</strong>
        </Card>
      )}

      {(level === LEVELS.UG || level === LEVELS.PG || level === LEVELS.INDIVIDUAL) && (
        <ExamSelection
          eligibleExamLevels={eligibleExamLevels}
          register={register}
          errors={errors}
          selectedSpecialization={selectedSpecialization}
          setSelectedSpecialization={setSelectedSpecialization}
          selectedExamLevel={selectedExamLevel}
          setSelectedExamLevel={setSelectedExamLevel}
          examSelections={examSelections}
          handleAddSelection={handleAddSelection}
          handleRemoveSelection={handleRemoveSelection}
          totalAmount={totalAmount}
        />
      )}
    </>
  );
};

export default EducationInformation;
